/**
 * Browser side of the WebAuthn ceremonies (`docs/WebAuthn.md`).
 *
 * ⚠ **The whole job here is base64url ↔ ArrayBuffer.** `navigator.credentials` speaks
 * `ArrayBuffer`; JSON does not, so `webauthn-rs` encodes every binary field as base64url and
 * expects the same back. A field missed in either direction fails at the *authenticator*, with
 * a `NotAllowedError` that says nothing about which field — so the conversions are centralised
 * here rather than inlined per call site.
 *
 * Hand-written rather than pulling in `@simplewebauthn/browser`: this is the entire surface we
 * need, and the two ceremonies below are the only callers.
 */

/** base64url (no padding, `-_` alphabet) → bytes. */
function fromBase64Url(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/** bytes → base64url. The server rejects standard base64, so the substitution is required. */
function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Whether this browser can run a ceremony at all. */
export function isWebAuthnAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

/**
 * A ceremony the user dismissed, or one this browser cannot run.
 *
 * Distinguished from a *failure* because the two want different words: a dismissed prompt is
 * "try again", a broken one is "use a recovery code". The DOM reports both as
 * `NotAllowedError`, so this is the best line that can be drawn — and it is drawn toward
 * "try again", the recoverable reading.
 */
export class CeremonyDismissed extends Error {}

/**
 * The ceremony could not be attempted — malformed options, an unsupported combination, an
 * insecure origin.
 *
 * ⚠ **Kept apart from `CeremonyDismissed`, because collapsing them hides the only diagnosable
 * failures.** A `NotSupportedError` from incongruent creation options reads exactly like a user
 * cancelling if both become "dismissed", and the user is told to try again at something that
 * cannot ever succeed. The original error is preserved as `cause` so it reaches the console.
 */
export class CeremonyUnsupported extends Error {
  constructor(message: string, cause: unknown) {
    super(message, { cause });
  }
}

/**
 * `NotAllowedError` is the only status the spec gives for "the user said no", and browsers
 * deliberately use it for several privacy-preserving refusals too. Everything else — most
 * usefully `NotSupportedError` and `SecurityError` — names a real cause worth surfacing.
 */
function classify(e: unknown, dismissedMessage: string): Error {
  const name = (e as { name?: string } | undefined)?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') return new CeremonyDismissed(dismissedMessage);
  return new CeremonyUnsupported(
    `This browser refused the request (${name ?? 'unknown error'}).`,
    e,
  );
}

/** The challenge as the server sends it: already wrapped in `publicKey`. */
interface ServerChallenge {
  publicKey: Record<string, unknown>;
}

function decodeDescriptors(list: unknown): unknown {
  if (!Array.isArray(list)) return list;
  return list.map((item: Record<string, unknown>) => ({ ...item, id: fromBase64Url(item.id as string) }));
}

/**
 * Run an authentication ceremony and return the credential in the shape the server parses.
 *
 * `challenge` is passed through from the server untouched — including `allowCredentials`, which
 * may contain **fabricated** entries for an address with no passkeys (§4.2). Filtering or
 * validating it here would defeat that, so it is decoded and handed to the authenticator as-is.
 *
 * @throws {CeremonyDismissed} when the user cancels or no usable credential is present.
 */
export async function getAssertion(challenge: ServerChallenge): Promise<unknown> {
  const options = { ...challenge.publicKey } as Record<string, unknown>;
  options.challenge = fromBase64Url(options.challenge as string);
  if (options.allowCredentials) options.allowCredentials = decodeDescriptors(options.allowCredentials);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey: options as unknown as PublicKeyCredentialRequestOptions,
    })) as PublicKeyCredential | null;
  } catch (e) {
    throw classify(e, 'The security key prompt was dismissed.');
  }
  if (!credential) throw new CeremonyDismissed('No credential was provided.');

  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    // `userHandle` is absent for a non-discoverable credential and present for a passkey.
    // Sent as null rather than omitted, because the server distinguishes the two.
    response: {
      authenticatorData: toBase64Url(response.authenticatorData),
      clientDataJSON: toBase64Url(response.clientDataJSON),
      signature: toBase64Url(response.signature),
      userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
    },
    extensions: credential.getClientExtensionResults(),
  };
}

/**
 * Run a registration ceremony and return the credential in the shape the server parses.
 *
 * @throws {CeremonyDismissed} when the user cancels.
 */
export async function createCredential(challenge: ServerChallenge): Promise<unknown> {
  const options = { ...challenge.publicKey } as Record<string, unknown>;
  options.challenge = fromBase64Url(options.challenge as string);
  const user = { ...(options.user as Record<string, unknown>) };
  user.id = fromBase64Url(user.id as string);
  options.user = user;
  if (options.excludeCredentials) options.excludeCredentials = decodeDescriptors(options.excludeCredentials);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: options as unknown as PublicKeyCredentialCreationOptions,
    })) as PublicKeyCredential | null;
  } catch (e) {
    throw classify(e, 'The security key prompt was dismissed.');
  }
  if (!credential) throw new CeremonyDismissed('No credential was created.');

  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: toBase64Url(response.attestationObject),
      clientDataJSON: toBase64Url(response.clientDataJSON),
    },
    extensions: credential.getClientExtensionResults(),
  };
}
